// ── UpdateScheduledPaymentsPage ──
import { useState, useEffect, useRef } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, SectionHeader, Td, Th } from "../../../shared/ui";
import { MonthEndNotice, ReforecastWarning, inMonthEndLock,
         ProceedBtn, CancelBtn } from "../../../shared/monthEndLock";
import { useApp } from "../../../shared/appContext";

export const UpdateScheduledPaymentsPage = () => {
    const { api, error } = useApp();
          const [starting, setStarting] = useState(false);
          const [batchId,  setBatchId]  = useState(null);
          const [status,   setStatus]   = useState(null);
          const [err,      setErr]      = useState(null);
          const [lockAsk,  setLockAsk]  = useState(false);
          const pollRef = useRef(null);

          // ── THE MONTH-END QUESTION ─────────────────────────────────────────
          //
          // Different from the one on Portfolio Performance, on purpose. There a
          // rebuild is a side effect of wanting IRRs and there is a safe
          // alternative one click away. HERE THE REBUILD IS THE JOB — this is
          // the screen the servicer uses to forecast the month end in the first
          // place, two business days out. The first run inside the window is
          // exactly what is supposed to happen.
          //
          // So this does not offer an alternative and does not discourage. It
          // asks the one question only the person pressing it can answer: have
          // this month's invoices already gone out.
          const askStart = () => {
            if (starting || (status && !status.complete)) return;
            if (inMonthEndLock()) { setLockAsk(true); return; }
            startBatch();
          };

          const startBatch = async () => {
            setStarting(true); setErr(null); setBatchId(null); setStatus(null);
            try {
              const res = await api.post('updateScheduledPayments', {});
              if (!res.batchId) throw new Error('No batchId returned');
              setBatchId(res.batchId);
              setStatus({ total: res.total, pending: res.total, processing: 0, done: 0, error: 0, skipped: 0, complete: false, rows: [] });
              setStarting(false);
            } catch (e) {
              setErr(e.message); setStarting(false);
            }
          };

          useEffect(() => {
            if (!batchId) return;
            const poll = async () => {
              try {
                const s = await api.get('getBatchStatus?batchid=' + batchId);
                setStatus(s);
                if (s.complete) clearInterval(pollRef.current);
              } catch (e) { clearInterval(pollRef.current); }
            };
            poll();
            pollRef.current = setInterval(poll, 2000);
            return () => clearInterval(pollRef.current);
          }, [batchId]);

          const statusColor = s =>
            s === 'done'       ? '#0F6E56' :
            s === 'error'      ? '#993C1D' :
            s === 'skipped'    ? '#888780' :
            s === 'processing' ? '#185FA5' : '#BA7517';

          const statusBg = s =>
            s === 'done'       ? '#E1F5EE' :
            s === 'error'      ? '#FAECE7' :
            s === 'skipped'    ? '#F1EFE8' :
            s === 'processing' ? '#E6F1FB' : '#FAEEDA';

          const pct = status && status.total > 0
            ? Math.round(((status.done + status.error + status.skipped) / status.total) * 100)
            : 0;

          return <>
            <SectionHeader title="Update Scheduled Payments" />

            {lockAsk && (
              <ReforecastWarning
                onClose={() => setLockAsk(false)}
                lede={"This rebuilds every future scheduled payment for every active "
                    + "loan. You are inside the month-end window."}
                actions={<>
                  <ProceedBtn onClick={() => { setLockAsk(false); startBatch(); }}>
                    Run it — the invoices have not gone out
                  </ProceedBtn>
                  <CancelBtn onClick={() => setLockAsk(false)}>Not yet</CancelBtn>
                </>} />
            )}

            <Card style={{ padding:20, marginBottom:20 }}>
              <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginBottom:16 }}>
                Recalculates future scheduled principal, interest, and fee for all active loans
                using current balances and forward rates from the latest curve date.
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <Btn
                  variant="primary"
                  disabled={starting || (status && !status.complete)}
                  onClick={askStart}
                >
                  {starting ? 'Starting...' : status && !status.complete ? 'Running...' : '⚡ Update All Scheduled Payments'}
                </Btn>
              </div>
              <MonthEndNotice>
                Running this now is how that month end gets forecast — but running
                it <em>again</em> after the invoices are out will move a figure the
                borrowers have already been billed for.
              </MonthEndNotice>
              {err && <div style={{ marginTop:12, background:'#FAECE7', border:'0.5px solid #E8B4A8', borderRadius:6, padding:'10px 14px', fontSize:13, color:'#993C1D' }}>⚠️ {err}</div>}
            </Card>

            {status && <>
              <Card style={{ padding:20, marginBottom:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>
                    {status.complete ? '✅ Complete' : '⏳ Processing...'}
                  </div>
                  <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>
                    {status.done + status.error + status.skipped} / {status.total}
                  </div>
                </div>
                <div style={{ height:8, background:'var(--color-background-secondary)', borderRadius:4, marginBottom:16 }}>
                  <div style={{ height:8, width:pct+'%', background:B.cyan, borderRadius:4, transition:'width 0.3s ease' }} />
                </div>

                <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
                  {[
                    ['Pending',    status.pending,    '#BA7517', '#FAEEDA'],
                    ['Processing', status.processing, '#185FA5', '#E6F1FB'],
                    ['Done',       status.done,       '#0F6E56', '#E1F5EE'],
                    ['Error',      status.error,      '#993C1D', '#FAECE7'],
                    ['Skipped',    status.skipped,    '#888780', '#F1EFE8'],
                  ].map(([label, count, color, bg]) => count > 0 && (
                    <div key={label} style={{ background:bg, color, padding:'4px 12px', borderRadius:6, fontSize:12, fontWeight:500 }}>
                      {label}: {count}
                    </div>
                  ))}
                </div>

                {status.rows.length > 0 && (
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>
                      <Th>Loan</Th>
                      <Th>Status</Th>
                      <Th>Message</Th>
                      <Th>Updated</Th>
                    </tr></thead>
                    <tbody>
                      {status.rows.map(r => (
                        <tr key={r.id}
                          onMouseEnter={e=>e.currentTarget.style.background='var(--color-background-secondary)'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          <Td style={{ fontWeight:500 }}>{r.companyname}</Td>
                          <Td>
                            <span style={{ color:statusColor(r.status), background:statusBg(r.status), padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:500 }}>
                              {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                            </span>
                          </Td>
                          <Td style={{ color:'var(--color-text-secondary)', fontSize:12 }}>{r.message||'—'}</Td>
                          <Td style={{ color:'var(--color-text-secondary)', fontSize:12 }}>
                            {r.updatedat ? new Date(r.updatedat).toLocaleTimeString() : '—'}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </>}
          </>;
        };
